// src/hooks/useSerialScanner.ts
// Đọc mã QR / mã vạch từ MÁY QUÉT nối qua CỔNG COM (USB Serial / RS-232 emulation) bằng Web Serial API.
//
// Máy quét cầm tay ở chế độ "USB Serial" gửi nội dung mã rồi kết thúc bằng 1 ký tự hậu tố — mặc định
// thường là CR (\r), có máy dùng CRLF (\r\n) hoặc TAB (\t), cũng có máy KHÔNG gắn hậu tố. Vòng đọc
// dưới đây xử lý cả 3 trường hợp: tách theo CR/LF/TAB, hoặc flush khi im lặng (máy không gắn hậu tố).
//
// Lưu ý: Web Serial API chỉ khả dụng trên Chrome/Edge và khi trang chạy trên HTTPS (hoặc localhost).
// Với trình duyệt không hỗ trợ, hook trả về supported = false để UI chuyển sang kiểu "bàn phím (USB HID)".

import { useCallback, useEffect, useRef, useState } from 'react';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('hook_use_serial_scanner');

// Web Serial chưa có sẵn trong lib.dom.d.ts của phiên bản TS đang dùng — khai báo tối thiểu tại chỗ.
interface SerialPortInfoLike {
  usbVendorId?: number;
  usbProductId?: number;
}
interface SerialSignalsLike {
  dataTerminalReady?: boolean;
  requestToSend?: boolean;
}
interface SerialOpenOptionsLike {
  baudRate: number;
  dataBits?: number;
  stopBits?: number;
  parity?: 'none' | 'even' | 'odd';
  bufferSize?: number;
  flowControl?: 'none' | 'hardware';
}
interface SerialPortLike {
  open(options: SerialOpenOptionsLike): Promise<void>;
  close(): Promise<void>;
  setSignals?: (signals: SerialSignalsLike) => Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
  getInfo?: () => SerialPortInfoLike;
}
interface SerialLike {
  requestPort(): Promise<SerialPortLike>;
  getPorts(): Promise<SerialPortLike[]>;
  addEventListener(type: 'connect' | 'disconnect', listener: () => void): void;
  removeEventListener(type: 'connect' | 'disconnect', listener: () => void): void;
}

const getSerial = (): SerialLike | undefined =>
  typeof navigator !== 'undefined'
    ? (navigator as unknown as { serial?: SerialLike }).serial
    : undefined;

// CHỈ dùng khi máy quét CHƯA TỪNG gửi hậu tố (CR/LF/TAB) trên kết nối này: chờ im lặng đủ lâu để một
// lần truyền hoàn tất rồi mới coi là hết 1 mã. Ngay khi thấy hậu tố lần đầu -> KHOÁ hẳn cơ chế này.
const NO_SUFFIX_IDLE_MS = 300;
// Bỏ ký tự điều khiển (NUL 0x00 .. US 0x1F, DEL 0x7F) như STX/ETX/CR/LF còn sót trong 1 lần quét.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = new RegExp('[\\x00-\\x1F\\x7F]', 'g');

export type SerialScannerStatus = 'unsupported' | 'idle' | 'connecting' | 'connected' | 'error';

interface UseSerialScannerOptions {
  // Máy quét USB CDC thường không phụ thuộc baud, nhưng có máy RS-232 thật thì cần đúng — Web Serial
  // luôn bắt buộc truyền vào.
  baudRate?: number;
  onScan: (code: string) => void;
  onError?: (message: string) => void;
}

interface UseSerialScannerResult {
  supported: boolean;
  status: SerialScannerStatus;
  portLabel: string;
  rxCount: number; // số LẦN QUÉT hoàn chỉnh đã bóc tách được
  rxBytes: number; // tổng số BYTE thô đã đọc từ cổng — phân biệt "cổng không gửi gì" vs "gửi nhưng không bóc được"
  lastRx: string; // preview vài ký tự gần nhất đọc được (chẩn đoán sai baud / rác)
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export function useSerialScanner({
  baudRate = 9600,
  onScan,
  onError,
}: UseSerialScannerOptions): UseSerialScannerResult {
  const supported = !!getSerial();
  const [status, setStatus] = useState<SerialScannerStatus>(supported ? 'idle' : 'unsupported');
  const [portLabel, setPortLabel] = useState('');
  const [rxCount, setRxCount] = useState(0);
  const [rxBytes, setRxBytes] = useState(0);
  const [lastRx, setLastRx] = useState('');

  // Giữ tham chiếu callback mới nhất để vòng đọc (chạy dài) luôn gọi đúng bản.
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const portRef = useRef<SerialPortLike | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const keepReadingRef = useRef(false);

  const disconnect = useCallback(async () => {
    keepReadingRef.current = false;
    const reader = readerRef.current;
    readerRef.current = null;
    if (reader) {
      try { await reader.cancel(); } catch { /* reader có thể đã đóng */ }
      try { reader.releaseLock(); } catch { /* ignore */ }
    }
    const port = portRef.current;
    portRef.current = null;
    if (port) {
      try { await port.close(); } catch { /* cổng có thể đã bị rút */ }
    }
    setPortLabel('');
    setStatus((prev) => (prev === 'unsupported' ? prev : 'idle'));
  }, []);

  // Vòng đọc dữ liệu liên tục từ cổng: gom byte -> chuỗi, tách thành từng lần quét.
  //  - Ưu tiên: tách theo HẬU TỐ CR/LF/TAB. Thấy hậu tố 1 lần -> KHOÁ vĩnh viễn flush-theo-im-lặng
  //    để token dài / truyền chia gói không bị pause cắt đôi.
  //  - Chưa hề thấy hậu tố: flush sau khi im lặng NO_SUFFIX_IDLE_MS, mỗi gói byte đều dời hẹn giờ.
  const readLoop = useCallback(async (port: SerialPortLike) => {
    const decoder = new TextDecoder();
    let buffer = '';
    let terminatorSeen = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const clearIdle = () => {
      if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    };
    // Chuẩn hoá + phát 1 lần quét: bỏ ký tự điều khiển còn sót, bỏ khoảng trắng thừa.
    const emit = (chunk: string) => {
      const line = chunk.replace(CONTROL_CHARS, '').trim();
      if (!line) return;
      setRxCount((n) => n + 1);
      onScanRef.current(line);
    };
    const idleFlush = () => {
      idleTimer = null;
      if (!terminatorSeen && buffer.trim()) {
        emit(buffer);
        buffer = '';
      }
    };
    const armIdleFlush = () => {
      clearIdle();
      idleTimer = setTimeout(idleFlush, NO_SUFFIX_IDLE_MS);
    };

    while (keepReadingRef.current && port.readable) {
      const reader = port.readable.getReader();
      readerRef.current = reader;
      try {
        let finished = false;
        while (!finished) {
          const { value, done } = await reader.read();
          finished = done;
          if (!value || value.length === 0) continue;

          const chunk = decoder.decode(value, { stream: true });
          setRxBytes((n) => n + value.length);
          setLastRx(chunk.replace(CONTROL_CHARS, '·').slice(-48));
          buffer += chunk;

          // (1) Tách theo hậu tố CR / LF / TAB — kết thúc dứt khoát 1 lần quét.
          let sepIndex = buffer.search(/[\r\n\t]/);
          while (sepIndex >= 0) {
            terminatorSeen = true;
            clearIdle();
            emit(buffer.slice(0, sepIndex));
            buffer = buffer.slice(sepIndex + 1);
            sepIndex = buffer.search(/[\r\n\t]/);
          }

          // (2) Chỉ khi máy CHƯA HỀ gửi hậu tố: hẹn giờ flush sau khi im lặng.
          if (!terminatorSeen && buffer.length > 0) armIdleFlush();
        }
      } catch {
        // reader.read() ném lỗi khi bị cancel lúc ngắt kết nối — thoát vòng lặp êm.
      } finally {
        clearIdle();
        try { reader.releaseLock(); } catch { /* ignore */ }
      }
    }
  }, []);

  const connect = useCallback(async () => {
    const serial = getSerial();
    if (!serial) {
      setStatus('unsupported');
      onErrorRef.current?.(
        st('trinh_duyet_khong_ho_tro_web_text'),
      );
      return;
    }
    if (portRef.current) return;
    setStatus('connecting');
    try {
      const port = await serial.requestPort();
      // 8 data bit, no parity, 1 stop bit — cấu hình phổ biến nhất của máy quét USB Serial.
      await port.open({ baudRate, dataBits: 8, stopBits: 1, parity: 'none', bufferSize: 4096, flowControl: 'none' });
      // Một số máy quét Serial chỉ truyền dữ liệu khi thấy DTR/RTS được bật.
      try {
        await port.setSignals?.({ dataTerminalReady: true, requestToSend: true });
      } catch {
        /* nền tảng / máy không hỗ trợ điều khiển tín hiệu — bỏ qua */
      }
      portRef.current = port;
      keepReadingRef.current = true;
      setRxCount(0);
      setRxBytes(0);
      setLastRx('');

      const info = port.getInfo?.();
      setPortLabel(
        info?.usbVendorId
          ? st('cong_com_vid_value_pid_value_text', { value: info.usbVendorId.toString(16).toUpperCase(), value_2: (info.usbProductId ?? 0)
              .toString(16)
              .toUpperCase() })
          : st('da_ket_noi_cong_com_text'),
      );
      setStatus('connected');

      readLoop(port).finally(() => {
        // Vòng đọc kết thúc do rút cáp / đóng cổng ở tầng dưới -> dọn trạng thái, báo cho UI.
        if (keepReadingRef.current) {
          keepReadingRef.current = false;
          portRef.current = null;
          setPortLabel('');
          setStatus('idle');
          onErrorRef.current?.(st('may_quet_cong_com_da_ngat_text'));
        }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Người dùng bấm "Huỷ" ở hộp thoại chọn cổng -> không coi là lỗi.
      if (/no port selected|cancel/i.test(message)) {
        setStatus('idle');
        return;
      }
      setStatus('error');
      onErrorRef.current?.(message || st('khong_mo_duoc_cong_com_text'));
    }
  }, [baudRate, readLoop]);

  // Tự ngắt khi component unmount.
  useEffect(() => () => { void disconnect(); }, [disconnect]);

  // Máy bị rút cáp ở tầng hệ điều hành.
  useEffect(() => {
    const serial = getSerial();
    if (!serial) return undefined;
    const handleDisconnect = () => { void disconnect(); };
    serial.addEventListener('disconnect', handleDisconnect);
    return () => serial.removeEventListener('disconnect', handleDisconnect);
  }, [disconnect]);

  return { supported, status, portLabel, rxCount, rxBytes, lastRx, connect, disconnect };
}
